import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Loading, Heading, Text } from '@puppet/react-components';
import CommonForm from '@components/CommonForm';
import Breadcrumbs from '@components/Breadcrumbs';
import PageLayout from '@components/PageLayout';
import Cd4peError from '@components/Cd4peError';
import { useAppDispatch } from '@hooks/redux';
import alerts from '@state/ui/alerts';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import useWorkspaceName from '@hooks/useWorkspaceName';
import {
  useListIntegrationsV1,
  useUpdateGitlabV1,
  useUpdateGithubEnterpriseV1,
  useUpdateBitbucketServerV1,
  useUpdateAzureDevOpsServerV1,
} from '@services/cd4pe/vcs';
import getProviderDisplayName from '@utils/vcs/displayNameHelper';
import * as Provider from '@utils/vcs/providerConsts';
import { LINKS } from 'src/routes';

type TokenFormValues = {
  token: string;
};

type BbsFormValues = {
  username: string;
  password: string;
};

const EditSourceControl = () => {
  const { t } = useTranslation('codeDelivery');
  const navigate = useNavigate();
  const { provider } = useParams<{ provider: string }>();
  const workspaceId = useWorkspaceDomain();
  const workspaceName = useWorkspaceName();
  const appDispatch = useAppDispatch();

  const [showToken, setShowToken] = useState(false);
  const [tokenValues, setTokenValues] = useState<TokenFormValues>({
    token: '',
  });
  const [bbsValues, setBbsValues] = useState<BbsFormValues>({
    username: '',
    password: '',
  });

  // All four update hooks called unconditionally (hooks cannot be conditional)
  const updateGitlab = useUpdateGitlabV1();
  const updateGithubEnterprise = useUpdateGithubEnterpriseV1();
  const updateBitbucketServer = useUpdateBitbucketServerV1();
  const updateAzureDevOps = useUpdateAzureDevOpsServerV1();

  const { data, isLoading, error } = useListIntegrationsV1({ workspaceId });

  const integration = data?.vcsIntegrations?.find(
    (v) => v.provider === provider,
  );

  const providerDisplayName = getProviderDisplayName(provider ?? '');

  const navigateToList = () => {
    navigate(
      LINKS.settings.listSourceControl({ path: { workspace: workspaceName } }),
    );
  };

  const onSuccess = () => {
    appDispatch(
      alerts.actions.createAlert({
        type: 'Success',
        message: t('editSourceControl.successAlert', {
          provider: providerDisplayName,
        }),
      }),
    );
    navigateToList();
  };

  const isBbs = provider === Provider.BITBUCKET_SERVER;

  const headerText = isBbs
    ? t('editSourceControl.headerCredentials', {
        provider: providerDisplayName,
      })
    : t('editSourceControl.headerToken', { provider: providerDisplayName });

  const isSubmitDisabled = isBbs
    ? !bbsValues.username || !bbsValues.password
    : !tokenValues.token;

  const getMutationError = () => {
    if (isBbs) return updateBitbucketServer.error;
    if (provider === Provider.GITLAB) return updateGitlab.error;
    if (provider === Provider.GITHUB_ENTERPRISE)
      return updateGithubEnterprise.error;
    if (provider === Provider.AZURE_DEV_OPS) return updateAzureDevOps.error;
    return undefined;
  };
  const mutationError = getMutationError();

  const onSubmit = () => {
    const opts = { onSuccess };

    if (isBbs) {
      updateBitbucketServer.mutate(
        { workspaceId, requestBody: bbsValues },
        opts,
      );
    } else if (provider === Provider.GITLAB) {
      updateGitlab.mutate(
        { workspaceId, requestBody: { token: tokenValues.token } },
        opts,
      );
    } else if (provider === Provider.GITHUB_ENTERPRISE) {
      updateGithubEnterprise.mutate(
        { workspaceId, requestBody: { token: tokenValues.token } },
        opts,
      );
    } else if (provider === Provider.AZURE_DEV_OPS) {
      updateAzureDevOps.mutate(
        { workspaceId, requestBody: { token: tokenValues.token } },
        opts,
      );
    } else {
      // unsupported provider — render guard above prevents reaching here
    }
  };

  const breadcrumbs = [
    {
      displayName: t('editSourceControl.breadcrumb.settings'),
      linkDestination: LINKS.settings.root({
        path: { workspace: workspaceName },
      }),
    },
    {
      displayName: t('editSourceControl.breadcrumb.sourceControl'),
      linkDestination: LINKS.settings.listSourceControl({
        path: { workspace: workspaceName },
      }),
    },
    {
      displayName: headerText,
    },
  ];

  if (isLoading) {
    return <Loading data-testid="loading-spinner" size="large" />;
  }

  if (error) {
    return <Cd4peError error={error} />;
  }

  if (!integration) {
    return <Heading>{t('editSourceControl.notFound')}</Heading>;
  }

  const supportedProviders = [
    Provider.GITLAB,
    Provider.GITHUB_ENTERPRISE,
    Provider.BITBUCKET_SERVER,
    Provider.AZURE_DEV_OPS,
  ];

  if (!supportedProviders.includes(provider ?? '')) {
    return <Heading>{t('editSourceControl.notFound')}</Heading>;
  }

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageLayout.Breadcrumbs>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </PageLayout.Breadcrumbs>
        <Heading>{headerText}</Heading>
        <Text>{t('editSourceControl.description')}</Text>
      </PageLayout.Header>
      <PageLayout.Content>
        {isBbs ? (
          <CommonForm
            submittable
            cancellable
            className="edit-source-control-form"
            submitLabel={t('editSourceControl.submitButton')}
            cancelLabel={t('editSourceControl.cancelButton')}
            values={{ host: integration.host ?? '', ...bbsValues }}
            onChange={(_, values) =>
              setBbsValues({
                username: values.username ?? '',
                password: values.password ?? '',
              })
            }
            onSubmit={onSubmit}
            onCancel={navigateToList}
            submitDisabled={isSubmitDisabled}
          >
            <CommonForm.Section>
              <CommonForm.Section.Main>
                <div className="edit-source-control-field__wrapper">
                  <Form.Field
                    type="text"
                    name="host"
                    label={t('editSourceControl.field.host')}
                    value={integration.host ?? ''}
                    disabled
                  />
                </div>
                <div className="edit-source-control-field__wrapper">
                  <Form.Field
                    type="text"
                    name="username"
                    label={t('editSourceControl.field.username')}
                    required
                  />
                </div>
                <div className="edit-source-control-field__wrapper">
                  <Form.Field
                    type="password"
                    name="password"
                    label={t('editSourceControl.field.password')}
                    required
                  />
                </div>
              </CommonForm.Section.Main>
            </CommonForm.Section>
            <Cd4peError error={mutationError} />
          </CommonForm>
        ) : (
          <CommonForm
            submittable
            cancellable
            className="edit-source-control-form"
            submitLabel={t('editSourceControl.submitButton')}
            cancelLabel={t('editSourceControl.cancelButton')}
            values={{ host: integration.host ?? '', ...tokenValues }}
            onChange={(_, values) =>
              setTokenValues({ token: values.token ?? '' })
            }
            onSubmit={onSubmit}
            onCancel={navigateToList}
            submitDisabled={isSubmitDisabled}
          >
            <CommonForm.Section>
              <CommonForm.Section.Main>
                <div className="edit-source-control-field__wrapper">
                  <Form.Field
                    type="text"
                    name="host"
                    label={t('editSourceControl.field.host')}
                    value={integration.host ?? ''}
                    disabled
                  />
                </div>
                <div className="edit-source-control-field__wrapper">
                  <Form.Field
                    type={showToken ? 'text' : 'password'}
                    trailingButtonIcon="eye"
                    trailingButtonProps={{
                      'aria-label': t(
                        'editSourceControl.field.token.ariaLabel',
                        {
                          provider: providerDisplayName,
                        },
                      ),
                    }}
                    onClickTrailingButton={() => setShowToken(!showToken)}
                    name="token"
                    label={t('editSourceControl.field.token')}
                    required
                  />
                </div>
              </CommonForm.Section.Main>
            </CommonForm.Section>
            <Cd4peError error={mutationError} />
          </CommonForm>
        )}
      </PageLayout.Content>
    </PageLayout>
  );
};

export default EditSourceControl;
